import React, { useState } from "react";
import { Row, Col, Card, CardBody, Tooltip, Badge, Collapse } from "reactstrap";
import { formatNumber } from "../utils";

/**
 * InfoTooltip - Reusable tooltip wrapper for explaining metrics
 */
const InfoTooltip = ({ id, text, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <span
        id={id}
        style={{
          cursor: "help",
          borderBottom: "1px dotted #6c757d",
        }}
      >
        {children}
      </span>
      <Tooltip
        placement="top"
        isOpen={isOpen}
        target={id}
        toggle={() => setIsOpen(!isOpen)}
        delay={{ show: 100, hide: 0 }}
        style={{ maxWidth: "300px" }}
      >
        {text}
      </Tooltip>
    </>
  );
};

/**
 * SimpleHorizontalBarChart - Horizontal bar chart for margin breakdown
 */
const SimpleHorizontalBarChart = ({ segments }) => {
  return (
    <div style={{ width: '100%' }}>
      {segments.map((seg, idx) => (
        <div key={idx} style={{ marginBottom: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px'
          }}>
            <small style={{ fontSize: '0.75em', fontWeight: 500 }}>
              {seg.label}
            </small>
            <small style={{ fontSize: '0.75em', fontWeight: 600 }}>
              {seg.percentage.toFixed(1)}%
            </small>
          </div>
          <div style={{
            width: '100%',
            height: '24px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${seg.percentage}%`,
              height: '100%',
              backgroundColor: seg.color,
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * SummaryCards - Enhanced project summary cards with clear breakdowns and tooltips
 * 
 * Features:
 * - Clear labels showing what makes up revenue and costs
 * - Tooltips explaining each category on hover
 * - Expandable details INSIDE each card
 * - Highlights percentage-based commissions
 */
const SummaryCards = ({
  // Revenue
  ingresoTotal = 0,
  ingresoInstructores = 0,
  ingresoCursoCerrado = 0,
  ingresoPartidas = 0,
  ingresoAbierto = 0,
  ingresoGeneral = 0,
  
  // Costs
  costoTotal = 0,
  costoInstructores = 0,
  costoPartidas = 0,
  comisionAbierto = 0,
  adminCursoAbierto = 0,
  adminCursoCerrado = 0,
  proyectoConCausa = 0,
  comisionVenta = 0,
  
  // Counts
  numInstructores = 0,
  numEstudiantes = 0,
  numPartidas = 0,
  
  // Calculated
  utilidad = 0,
  margen = 0,

  // Profit distribution
  utilidadPartners = 0,
  utilidadLSSI = 0,

  // Original currency display
  originalCurrency = null,
  originalIngresoTotal = null,
  originalCostoTotal = null,
  originalUtilidad = null,

  // Revenue drill-down: parent may pass per-source detail arrays that get
  // rendered as a nested list under each expanded revenue line. Shape:
  //   { instructores: [{ label, sub, value, moneda }], cursoCerrado: [...], ... }
  revenueDetails = null,

  // Cost drill-down: same shape as revenueDetails, keyed by the cost
  // concepts (pagoInstructores, costoPartidas, comisionAbierto, etc.).
  // UPG-1: surfaces which instructor, partida, admin or commission drives
  // each cost concept so users don't need to hunt in the section tables.
  costDetails = null,

  // Click-through: parent handler that receives a section key
  // (e.g. "instructores", "partidas", "honorarios") and jumps to the
  // matching tab. When provided, each concept in the breakdowns becomes
  // a clickable link that scrolls the page to that section.
  onSectionClick = null,

  // Profit by currency breakdown (from backend)
  profitByCurrency = {},

  // Admin visibility
  isAdmin = false,
}) => {
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);
  const [showUtilidadBreakdown, setShowUtilidadBreakdown] = useState(false);
  const [showMargenBreakdown, setShowMargenBreakdown] = useState(false);
  // UPG-2: per-concept inline expansion inside the revenue card. The details
  // for each source (instructores, cursoCerrado, etc.) start collapsed and
  // reveal on chevron click without leaving the card. Clicking the concept
  // label itself keeps jumping to the section — two actions, two affordances.
  const [expandedConcepts, setExpandedConcepts] = useState({});
  const toggleConcept = (key) => setExpandedConcepts((prev) => ({ ...prev, [key]: !prev[key] }));

  // Helper to format currency
  const fmt = (value) => {
    if (value === null || value === undefined) return "0.00";
    return formatNumber(value);
  };

  // Tooltip descriptions for each category
  const tooltips = {
    ingresoTotal: "Suma total de todos los ingresos del proyecto: instructores, cursos, partidas e ingresos generales.",
    ingresoInstructores: "Ingresos generados por servicios de instructores (precio × horas × semanas).",
    ingresoCursoCerrado: "Ingresos de cursos cerrados (privados para una empresa).",
    ingresoAbierto: "Ingresos de inscripciones de estudiantes en cursos abiertos al público.",
    ingresoPartidas: "Ingresos adicionales: viáticos, materiales, hospedaje, etc.",
    ingresoGeneral: "Otros ingresos que no caen en las categorías anteriores.",
    costoTotal: "Suma total de todos los costos del proyecto incluyendo pagos a instructores, comisiones y gastos.",
    costoInstructores: "Pagos a instructores por sus servicios (costo × horas × semanas).",
    costoPartidas: "Gastos directos: viáticos, materiales, hospedaje, transporte, etc.",
    comisionAbierto: "Comisión pagada a vendedores por inscripciones en cursos abiertos. Se calcula como PORCENTAJE del ingreso del estudiante (ej: 10% de €950 = €95).",
    adminCursoAbierto: "Costo del administrador del curso abierto.",
    adminCursoCerrado: "Costo del administrador del curso cerrado.",
    proyectoConCausa: "Donación o aportación a causa social vinculada al proyecto.",
    comisionVenta: "Comisión por venta del proyecto o curso.",
    utilidad: "Ingreso Total menos Costo Total. Representa la ganancia bruta del proyecto.",
    margen: "Porcentaje de utilidad respecto al ingreso: (Utilidad ÷ Ingreso) × 100. Verde = excelente (≥80%), Azul = bueno (≥50%), Amarillo = regular (≥20%), Rojo = bajo (<20%).",
  };

  // Determine what makes up the revenue (non-zero items). The `key` matches
  // the corresponding entry in revenueDetails so the expanded card can
  // render per-source drill-down rows.
  const revenueBreakdown = [
    { key: "instructores",  label: "Instructores",   value: ingresoInstructores,   icon: "👨‍🏫", count: numInstructores, tooltip: tooltips.ingresoInstructores },
    { key: "cursoCerrado",  label: "Curso Cerrado",  value: ingresoCursoCerrado,   icon: "🎓",   tooltip: tooltips.ingresoCursoCerrado },
    { key: "partidas",      label: "Partidas",       value: ingresoPartidas,       icon: "📦",   count: numPartidas, tooltip: tooltips.ingresoPartidas },
    { key: "cursoAbierto",  label: "Curso Abierto",  value: ingresoAbierto,        icon: "👥",   count: numEstudiantes, tooltip: tooltips.ingresoAbierto },
    { key: "ingresoGeneral", label: "Ingreso General", value: ingresoGeneral,      icon: "💰",   tooltip: tooltips.ingresoGeneral },
  ].filter(item => item.value > 0);

  // Determine what makes up the costs (non-zero items). `key` matches the
  // parent's SECTION_TO_TAB mapping for click-through navigation.
  const costBreakdown = [
    { key: "pagoInstructores",  label: "Pago Instructores",   value: costoInstructores,   icon: "👨‍🏫", tooltip: tooltips.costoInstructores },
    { key: "costoPartidas",     label: "Costo Partidas",      value: costoPartidas,       icon: "📦",   tooltip: tooltips.costoPartidas },
    { key: "comisionAbierto",   label: "Comisión Vendedores", value: comisionAbierto,     icon: "💵",   highlight: true, tooltip: tooltips.comisionAbierto },
    { key: "adminCursoAbierto", label: "Admin Curso Abierto", value: adminCursoAbierto,   icon: "📋",   tooltip: tooltips.adminCursoAbierto },
    { key: "adminCursoCerrado", label: "Admin Curso Cerrado", value: adminCursoCerrado,   icon: "📝",   tooltip: tooltips.adminCursoCerrado },
    { key: "proyectoConCausa",  label: "Proyecto con Causa",  value: proyectoConCausa,    icon: "❤️",  tooltip: tooltips.proyectoConCausa },
    { key: "comisionVenta",     label: "Comisión Venta",      value: comisionVenta,       icon: "🤝",   tooltip: tooltips.comisionVenta },
  ].filter(item => item.value > 0);

  // Generate summary text for revenue
  const getRevenueSummary = () => {
    if (revenueBreakdown.length === 0) return "Sin ingresos";
    const parts = [];
    if (numEstudiantes > 0) parts.push(`${numEstudiantes} estudiantes`);
    if (numInstructores > 0) parts.push(`${numInstructores} instructores`);
    if (numPartidas > 0) parts.push(`${numPartidas} partidas`);
    return parts.length > 0 ? parts.join(" • ") : revenueBreakdown.map(r => r.label).join(", ");
  };

  // Generate summary text for costs
  const getCostSummary = () => {
    if (costBreakdown.length === 0) return "Sin costos";
    return costBreakdown.map(c => c.label).join(" • ");
  };

  // Margin color based on percentage
  const getMarginColor = () => {
    if (margen >= 80) return "#28a745";
    if (margen >= 50) return "#17a2b8";
    if (margen >= 20) return "#ffc107";
    return "#dc3545";
  };

  // Get margin rating text
  const getMarginRating = () => {
    if (margen >= 80) return "Excelente";
    if (margen >= 50) return "Bueno";
    if (margen >= 20) return "Regular";
    return "Bajo";
  };

  return (
    <div className="summary-cards-container">
      <Row>
        {/* INGRESO TOTAL */}
        <Col md={3} sm={6} className="mb-3">
          <Card 
            className="h-100 summary-card"
            style={{ 
              borderLeft: "4px solid #28a745",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onClick={() => setShowRevenueBreakdown(!showRevenueBreakdown)}
          >
            <CardBody className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: "1.2em" }}>💰</span>
                <h6 className="text-success mb-0 ms-2 text-uppercase" style={{ fontSize: "0.85em", fontWeight: 600 }}>
                  <InfoTooltip id="tooltip-ingreso-total" text={tooltips.ingresoTotal}>
                    Ingreso Total
                  </InfoTooltip>
                </h6>
                <Badge 
                  color="light" 
                  className="ml-auto"
                  style={{ fontSize: "0.7em", cursor: "pointer" }}
                  title="Click para ver desglose"
                >
                  {showRevenueBreakdown ? "▲" : "▼"}
                </Badge>
              </div>
              
              {/* Original currency if different */}
              {originalCurrency && originalIngresoTotal && (
                <small className="text-muted">
                  {originalCurrency}{fmt(originalIngresoTotal)}
                </small>
              )}
              
              {/* USD Total */}
              <h3 className="text-success mb-2" style={{ fontWeight: 700 }}>
                ${fmt(ingresoTotal)} USD
              </h3>
              
              {/* Summary of sources - hide when expanded */}
              {!showRevenueBreakdown && (
                <small className="text-muted mt-auto" style={{ fontSize: "0.75em" }}>
                  {getRevenueSummary()}
                </small>
              )}
              
              {/* ✅ BREAKDOWN INSIDE THE CARD */}
              <Collapse isOpen={showRevenueBreakdown}>
                <div 
                  className="mt-2 pt-2" 
                  style={{ 
                    borderTop: "1px solid #c3e6cb",
                    backgroundColor: "#f8fff8",
                    margin: "0 -1rem -1rem -1rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "0 0 4px 4px"
                  }}
                >
                  <small className="text-success d-block mb-2" style={{ fontWeight: 600 }}>
                    Desglose de Ingresos:
                  </small>
                  {revenueBreakdown.map((item, idx) => {
                    const detailRows = (revenueDetails && item.key && revenueDetails[item.key]) || [];
                    const hasDetails = detailRows.length > 0;
                    const isOpen = !!expandedConcepts[item.key];
                    const clickable = onSectionClick && item.key;
                    const handleGo = clickable ? (e) => { e.stopPropagation(); onSectionClick(item.key); } : undefined;
                    const handleToggle = (e) => { e.stopPropagation(); toggleConcept(item.key); };
                    return (
                      <div
                        key={idx}
                        onClick={(e) => e.stopPropagation()}
                        style={{ borderBottom: idx < revenueBreakdown.length - 1 ? "1px solid #e9ecef" : "none", padding: "4px 0" }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
                          <small style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1 }}>
                            {hasDetails ? (
                              <button
                                type="button"
                                onClick={handleToggle}
                                aria-label={isOpen ? `Ocultar detalle de ${item.label}` : `Ver detalle de ${item.label}`}
                                title={isOpen ? "Ocultar detalle" : "Ver detalle"}
                                style={{
                                  background: "transparent", border: "none", padding: 0, cursor: "pointer",
                                  color: "#6b7280", fontSize: "0.85em", lineHeight: 1, width: 14,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                }}
                              >
                                {isOpen ? "▾" : "▸"}
                              </button>
                            ) : (
                              <span style={{ width: 14, display: "inline-block" }} />
                            )}
                            <InfoTooltip id={`tooltip-rev-${idx}`} text={item.tooltip}>
                              <span
                                onClick={handleGo}
                                role={clickable ? "button" : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGo(e); } } : undefined}
                                title={clickable ? `Ir a la sección ${item.label}` : undefined}
                                style={clickable ? {
                                  cursor: "pointer",
                                  textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2,
                                } : undefined}
                              >
                                {item.icon} {item.label}
                              </span>
                            </InfoTooltip>
                            {item.count > 0 && (
                              <Badge color="success" pill className="ms-1" style={{ fontSize: "0.65em" }}>
                                {item.count}
                              </Badge>
                            )}
                          </small>
                          <small className="text-success" style={{ fontWeight: 500 }}>${fmt(item.value)}</small>
                        </div>
                        {hasDetails && isOpen && (
                          <div style={{ marginLeft: 20, marginTop: 4, borderLeft: "2px solid #e5e7eb", paddingLeft: 10 }}>
                            {detailRows.map((row, i) => (
                              <div
                                key={i}
                                className="d-flex justify-content-between align-items-baseline"
                                style={{ padding: "2px 0", fontSize: "0.75rem", color: "#374151", gap: 8 }}
                              >
                                <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.label}>
                                  {row.label}
                                  {row.sub && (
                                    <span style={{ color: "#6b7280", marginLeft: 6 }}>· {row.sub}</span>
                                  )}
                                </span>
                                <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                                  {row.moneda || "USD"} {fmt(row.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </Col>

        {/* COSTO TOTAL */}
        <Col md={3} sm={6} className="mb-3">
          <Card 
            className="h-100 summary-card"
            style={{ 
              borderLeft: "4px solid #dc3545",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onClick={() => setShowCostBreakdown(!showCostBreakdown)}
          >
            <CardBody className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: "1.2em" }}>💸</span>
                <h6 className="text-danger mb-0 ms-2 text-uppercase" style={{ fontSize: "0.85em", fontWeight: 600 }}>
                  <InfoTooltip id="tooltip-costo-total" text={tooltips.costoTotal}>
                    Costo Total
                  </InfoTooltip>
                </h6>
                <Badge 
                  color="light" 
                  className="ml-auto"
                  style={{ fontSize: "0.7em", cursor: "pointer" }}
                  title="Click para ver desglose"
                >
                  {showCostBreakdown ? "▲" : "▼"}
                </Badge>
              </div>
              
              {/* Original currency if different */}
              {originalCurrency && originalCostoTotal && (
                <small className="text-muted">
                  {originalCurrency}{fmt(originalCostoTotal)}
                </small>
              )}

              {/* USD Total */}
              <h3 className="text-danger mb-2" style={{ fontWeight: 700 }}>
                ${fmt(costoTotal)} USD
              </h3>
              
              {/* Summary of costs - hide when expanded */}
              {!showCostBreakdown && (
                <small className="text-muted mt-auto" style={{ fontSize: "0.75em" }}>
                  {getCostSummary()}
                </small>
              )}
              
              {/* ✅ BREAKDOWN INSIDE THE CARD */}
              <Collapse isOpen={showCostBreakdown}>
                <div 
                  className="mt-2 pt-2" 
                  style={{ 
                    borderTop: "1px solid #f5c6cb",
                    backgroundColor: "#fff8f8",
                    margin: "0 -1rem -1rem -1rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "0 0 4px 4px"
                  }}
                >
                  <small className="text-danger d-block mb-2" style={{ fontWeight: 600 }}>
                    Desglose de Costos:
                  </small>
                  {costBreakdown.map((item, idx) => {
                    const detailRows = (costDetails && item.key && costDetails[item.key]) || [];
                    const hasDetails = detailRows.length > 0;
                    const isOpen = !!expandedConcepts[`cost-${item.key}`];
                    const clickable = onSectionClick && item.key;
                    const handleGo = clickable ? (e) => { e.stopPropagation(); onSectionClick(item.key); } : undefined;
                    const handleToggle = (e) => { e.stopPropagation(); toggleConcept(`cost-${item.key}`); };
                    return (
                      <div
                        key={idx}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          borderBottom: idx < costBreakdown.length - 1 ? "1px solid #e9ecef" : "none",
                          backgroundColor: item.highlight ? "#fff3cd" : "transparent",
                          padding: item.highlight ? "4px 8px" : "4px 0",
                          borderRadius: item.highlight ? "4px" : "0",
                          margin: item.highlight ? "2px -8px" : "0",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
                          <small style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1 }}>
                            {hasDetails ? (
                              <button
                                type="button"
                                onClick={handleToggle}
                                aria-label={isOpen ? `Ocultar detalle de ${item.label}` : `Ver detalle de ${item.label}`}
                                title={isOpen ? "Ocultar detalle" : "Ver detalle"}
                                style={{
                                  background: "transparent", border: "none", padding: 0, cursor: "pointer",
                                  color: "#6b7280", fontSize: "0.85em", lineHeight: 1, width: 14,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                }}
                              >
                                {isOpen ? "▾" : "▸"}
                              </button>
                            ) : (
                              <span style={{ width: 14, display: "inline-block" }} />
                            )}
                            <InfoTooltip id={`tooltip-cost-${idx}`} text={item.tooltip}>
                              <span
                                onClick={handleGo}
                                role={clickable ? "button" : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGo(e); } } : undefined}
                                title={clickable ? `Ir a la sección ${item.label}` : undefined}
                                style={clickable ? {
                                  cursor: "pointer",
                                  textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2,
                                } : undefined}
                              >
                                {item.icon} {item.label}
                              </span>
                            </InfoTooltip>
                            {item.highlight && (
                              <Badge color="warning" className="ms-1" style={{ fontSize: "0.6em" }} title="Calculado como porcentaje del ingreso">
                                %
                              </Badge>
                            )}
                          </small>
                          <small className="text-danger" style={{ fontWeight: 500 }}>${fmt(item.value)}</small>
                        </div>
                        {hasDetails && isOpen && (
                          <div style={{ marginLeft: 20, marginTop: 4, borderLeft: "2px solid #f5c6cb", paddingLeft: 10 }}>
                            {detailRows.map((row, i) => (
                              <div
                                key={i}
                                className="d-flex justify-content-between align-items-baseline"
                                style={{ padding: "2px 0", fontSize: "0.75rem", color: "#374151", gap: 8 }}
                              >
                                <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.label}>
                                  {row.label}
                                  {row.sub && (
                                    <span style={{ color: "#6b7280", marginLeft: 6 }}>· {row.sub}</span>
                                  )}
                                </span>
                                <span style={{ color: "#6b7280", whiteSpace: "nowrap" }}>
                                  {row.moneda || "USD"} {fmt(row.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Cost percentage note */}
                  {ingresoTotal > 0 && costoTotal > 0 && (
                    <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #f5c6cb" }}>
                      <small className="text-muted">
                        = {((costoTotal / ingresoTotal) * 100).toFixed(1)}% del ingreso total
                      </small>
                    </div>
                  )}
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </Col>

        {/* UTILIDAD */}
        <Col md={3} sm={6} className="mb-3">
          <Card
            className="h-100 summary-card"
            style={{
              borderLeft: "4px solid #17a2b8",
              cursor: isAdmin ? "pointer" : "default",
              transition: "all 0.2s"
            }}
            onClick={isAdmin ? () => setShowUtilidadBreakdown(!showUtilidadBreakdown) : undefined}
          >
            <CardBody className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: "1.2em" }}>📈</span>
                <h6 className="text-info mb-0 ms-2 text-uppercase" style={{ fontSize: "0.85em", fontWeight: 600 }}>
                  <InfoTooltip id="tooltip-utilidad" text={tooltips.utilidad}>
                    Utilidad
                  </InfoTooltip>
                </h6>
                {isAdmin && (
                  <Badge
                    color="light"
                    className="ml-auto"
                    style={{ fontSize: "0.7em", cursor: "pointer" }}
                    title="Click para ver desglose"
                  >
                    {showUtilidadBreakdown ? "▲" : "▼"}
                  </Badge>
                )}
              </div>

              {/* Original currency if different */}
              {originalCurrency && originalUtilidad !== null && !showUtilidadBreakdown && (
                <small className="text-muted">
                  {originalCurrency}{fmt(originalUtilidad)}
                </small>
              )}

              {/* Profit amount in USD */}
              <h3 className={utilidad >= 0 ? "text-info" : "text-danger"} style={{ fontWeight: 700 }}>
                ${fmt(utilidad)} USD
              </h3>

              {/* Calculation breakdown - hide when expanded */}
              {!showUtilidadBreakdown && (
                <small className="text-muted mt-auto" style={{ fontSize: "0.75em" }}>
                  <InfoTooltip id="tooltip-utilidad-calc" text="Ingreso Total - Costo Total = Utilidad">
                    ${fmt(ingresoTotal)} - ${fmt(costoTotal)}
                  </InfoTooltip>
                </small>
              )}

              {/* BREAKDOWN INSIDE THE CARD - admin only */}
              {isAdmin && (
                <Collapse isOpen={showUtilidadBreakdown}>
                  <div
                    className="mt-2 pt-2"
                    style={{
                      borderTop: "1px solid #bee5eb",
                      backgroundColor: "#f8feff",
                      margin: "0 -1rem -1rem -1rem",
                      padding: "0.75rem 1rem",
                      borderRadius: "0 0 4px 4px"
                    }}
                  >
                    <small className="text-info d-block mb-2" style={{ fontWeight: 600 }}>
                      <InfoTooltip
                        id="tooltip-utilidad-reparto"
                        text="Refleja lo asignado a partners en la sección Utilidad (etapas + asignación de instructores). Cuando el proyecto aún no tiene asignaciones se muestra el diseño 50/50 como referencia."
                      >
                        Distribución de Utilidad
                      </InfoTooltip>:
                    </small>

                    {/* Partners */}
                    <div
                      className="d-flex justify-content-between align-items-center py-1"
                      style={{ borderBottom: "1px solid #e9ecef", cursor: onSectionClick ? "pointer" : "default" }}
                      onClick={onSectionClick ? (e) => { e.stopPropagation(); onSectionClick('utilidadPartners'); } : undefined}
                      role={onSectionClick ? "button" : undefined}
                      tabIndex={onSectionClick ? 0 : undefined}
                      onKeyDown={onSectionClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSectionClick('utilidadPartners'); } } : undefined}
                      title={onSectionClick ? "Ir a Utilidad" : undefined}
                    >
                      <small>
                        <InfoTooltip id="tooltip-util-partners" text="Utilidad distribuida entre todos los partners del proyecto según etapas del pipeline y asignaciones">
                          <span style={onSectionClick ? { textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2 } : undefined}>
                            👥 Partners
                          </span>
                        </InfoTooltip>
                      </small>
                      <small className="text-info" style={{ fontWeight: 500 }}>${fmt(utilidadPartners)}</small>
                    </div>

                    {/* LSSI */}
                    <div
                      className="d-flex justify-content-between align-items-center py-1"
                      style={{ cursor: onSectionClick ? "pointer" : "default" }}
                      onClick={onSectionClick ? (e) => { e.stopPropagation(); onSectionClick('utilidadLSSI'); } : undefined}
                      role={onSectionClick ? "button" : undefined}
                      tabIndex={onSectionClick ? 0 : undefined}
                      onKeyDown={onSectionClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSectionClick('utilidadLSSI'); } } : undefined}
                      title={onSectionClick ? "Ir a Utilidad" : undefined}
                    >
                      <small>
                        <InfoTooltip id="tooltip-util-lssi" text="Utilidad que queda para LSSI después de distribuir a partners">
                          <span style={onSectionClick ? { textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: 2 } : undefined}>
                            🏢 LSSI
                          </span>
                        </InfoTooltip>
                      </small>
                      <small className="text-info" style={{ fontWeight: 500 }}>${fmt(utilidadLSSI)}</small>
                    </div>

                    {/* Percentages */}
                    {utilidad > 0 && (
                      <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #bee5eb" }}>
                        <small className="text-muted d-block">
                          Partners: {((utilidadPartners / utilidad) * 100).toFixed(1)}% •
                          LSSI: {((utilidadLSSI / utilidad) * 100).toFixed(1)}%
                        </small>
                      </div>
                    )}
                  </div>
                </Collapse>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* MARGEN */}
        <Col md={3} sm={6} className="mb-3">
          <Card
            className="h-100 summary-card"
            style={{
              borderLeft: `4px solid ${getMarginColor()}`,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onClick={() => setShowMargenBreakdown(!showMargenBreakdown)}
          >
            <CardBody className="d-flex flex-column">
              <div className="d-flex align-items-center mb-2">
                <span style={{ fontSize: "1.2em" }}>📊</span>
                <h6 className="mb-0 ms-2 text-uppercase" style={{ fontSize: "0.85em", fontWeight: 600, color: getMarginColor() }}>
                  <InfoTooltip id="tooltip-margen" text={tooltips.margen}>
                    Margen
                  </InfoTooltip>
                </h6>
                {!showMargenBreakdown && (
                  <Badge
                    style={{ backgroundColor: getMarginColor(), marginLeft: "auto" }}
                    className="ml-auto"
                  >
                    {getMarginRating()}
                  </Badge>
                )}
                {showMargenBreakdown && (
                  <Badge
                    color="light"
                    className="ml-auto"
                    style={{ fontSize: "0.7em", cursor: "pointer" }}
                    title="Click para ocultar"
                  >
                    ▲
                  </Badge>
                )}
              </div>

              {/* Margin percentage */}
              <h3 style={{ fontWeight: 700, color: getMarginColor() }}>
                {margen.toFixed(2)}%
              </h3>

              {/* Progress bar - hide when expanded */}
              {!showMargenBreakdown && (
                <div className="mt-auto">
                  <div style={{
                    height: "8px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    overflow: "hidden"
                  }}>
                    <div style={{
                      width: `${Math.min(100, margen)}%`,
                      height: "100%",
                      backgroundColor: getMarginColor(),
                      transition: "width 0.3s ease"
                    }} />
                  </div>
                  <small className="text-muted mt-1 d-block" style={{ fontSize: "0.7em" }}>
                    Meta: ≥80%
                  </small>
                </div>
              )}

              {/* PIE CHART BREAKDOWN - admin only shows Partners/LSSI split */}
              <Collapse isOpen={showMargenBreakdown}>
                <div
                  className="mt-2 pt-2"
                  style={{
                    borderTop: `1px solid ${getMarginColor()}33`,
                    backgroundColor: "#fafafa",
                    margin: "0 -1rem -1rem -1rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "0 0 4px 4px"
                  }}
                >
                  <small className="d-block mb-2 text-center" style={{ fontWeight: 600, color: getMarginColor() }}>
                    Distribución del Proyecto:
                  </small>

                  {ingresoTotal > 0 && (
                    <SimpleHorizontalBarChart
                      segments={[
                        {
                          label: 'Costo Directo',
                          percentage: (costoTotal / ingresoTotal) * 100,
                          color: '#dc3545'
                        },
                        ...(isAdmin ? [
                          {
                            label: 'Utilidad Partners',
                            percentage: (utilidadPartners / ingresoTotal) * 100,
                            color: '#6f42c1'
                          },
                          {
                            label: 'Utilidad LSSI',
                            percentage: (utilidadLSSI / ingresoTotal) * 100,
                            color: '#17a2b8'
                          }
                        ] : [
                          {
                            label: 'Utilidad',
                            percentage: (utilidad / ingresoTotal) * 100,
                            color: '#17a2b8'
                          }
                        ])
                      ].filter(seg => seg.percentage > 0)}
                    />
                  )}

                  <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #dee2e6" }}>
                    <small className="text-muted d-block text-center">
                      Margen Total: <strong style={{ color: getMarginColor() }}>{margen.toFixed(1)}%</strong>
                    </small>
                  </div>
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .summary-cards-container .ms-1 {
          margin-left: 0.25rem;
        }
        .summary-cards-container .ms-2 {
          margin-left: 0.5rem;
        }
        .summary-cards-container .ml-auto {
          margin-left: auto;
        }
      `}</style>
    </div>
  );
};

export default SummaryCards;